import { GeoJSONSourceSpecification } from 'maplibre-gl';
import maplibregl from 'maplibre-gl';
import { Element_Base, Element_LineString_StyleMetadata_ModelUtil, Element_Point_StyleMetadata, Element_Polygon_StyleMetadata_ModelUtil } from '~/common/typescript/models/Element';
import { IconUtil } from '~/utils/IconUtil';
import { Icon } from '~/components/common/IconSelector/svg/shapes';
import { UUIDUtils } from '~/common/typescript/utils/UuidUtils';
import { Element_Point_StyleMetadata_ModelUtil } from '~/common/typescript/models/Element';

interface ElementHash {
  geometry: string;
  style: string;
  info: string;
}

interface ElementDiff {
  added: Element_Base[];
  removed: Element_Base[];
  updated: {
    element: Element_Base;
    changes: {
      geometry?: boolean;
      style?: boolean;
      info?: boolean;
    };
  }[];
  unchanged: Element_Base[];
}

export const ElementMapUtilOptimized = {
  // Cache để tracking elements hiện tại trên map
  private_elementsOnMap: new Map<string, Element_Base>(),
  private_elementHashes: new Map<string, ElementHash>(),

  /**
   * Main optimized update function - chỉ update elements thay đổi
   */
  updateElements: async (newElements: Element_Base[]) => {
    const oldElements = Array.from(ElementMapUtilOptimized.private_elementsOnMap.values());
    const diff = ElementMapUtilOptimized.computeElementsDiff(oldElements, newElements);

    // Batch operations để minimize map re-renders
    await ElementMapUtilOptimized.batchUpdate(async () => {
      // 1. Remove deleted elements
      for (const element of diff.removed) {
        await ElementMapUtilOptimized.removeElementFromMap(element);
      }

      // 2. Add new elements
      for (const element of diff.added) {
        await ElementMapUtilOptimized.addElementToMap(element);
      }

      // 3. Update changed elements
      for (const { element, changes } of diff.updated) {
        await ElementMapUtilOptimized.updateElementOnMap(element, changes);
      }
    });

    // Update cache
    ElementMapUtilOptimized.private_elementsOnMap.clear();
    ElementMapUtilOptimized.private_elementHashes.clear();

    for (const element of newElements) {
      if (!element.is_deleted) {
        ElementMapUtilOptimized.private_elementsOnMap.set(element.uuid, element);
        ElementMapUtilOptimized.private_elementHashes.set(element.uuid, ElementMapUtilOptimized.computeElementHash(element));
      }
    }
  },

  /**
   * So sánh oldElements vs newElements và return diff
   */
  computeElementsDiff: (oldElements: Element_Base[], newElements: Element_Base[]): ElementDiff => {
    const oldMap = new Map<string, Element_Base>();
    const newMap = new Map<string, Element_Base>();

    // Build maps for fast lookup
    oldElements.forEach(el => {
      if (!el.is_deleted) oldMap.set(el.uuid, el);
    });
    newElements.forEach(el => {
      if (!el.is_deleted) newMap.set(el.uuid, el);
    });

    const added: Element_Base[] = [];
    const removed: Element_Base[] = [];
    const updated: { element: Element_Base; changes: any }[] = [];
    const unchanged: Element_Base[] = [];

    // Find added and updated elements
    for (const [uuid, newElement] of newMap) {
      const oldElement = oldMap.get(uuid);

      if (!oldElement) {
        // New element
        added.push(newElement);
      } else {
        // Check if element changed
        const changes = ElementMapUtilOptimized.getElementChanges(oldElement, newElement);
        if (Object.keys(changes).length > 0) {
          updated.push({ element: newElement, changes });
        } else {
          unchanged.push(newElement);
        }
      }
    }

    // Find removed elements
    for (const [uuid, oldElement] of oldMap) {
      if (!newMap.has(uuid)) {
        removed.push(oldElement);
      }
    }

    return { added, removed, updated, unchanged };
  },

  /**
   * Compute hash cho element để detect changes
   */
  computeElementHash: (element: Element_Base): ElementHash => {
    const geometry = element.geom || '';
    const metadata = element.metadata ? JSON.parse(element.metadata) : {};
    const style = JSON.stringify(metadata.style || {});
    const info = `${element.name || ''}|${element.description || ''}`;

    return {
      geometry: ElementMapUtilOptimized.hashString(geometry),
      style: ElementMapUtilOptimized.hashString(style),
      info: ElementMapUtilOptimized.hashString(info)
    };
  },

  /**
   * So sánh 2 elements và return changes
   */
  getElementChanges: (oldElement: Element_Base, newElement: Element_Base) => {
    const oldHash = ElementMapUtilOptimized.private_elementHashes.get(oldElement.uuid) ||
                   ElementMapUtilOptimized.computeElementHash(oldElement);
    const newHash = ElementMapUtilOptimized.computeElementHash(newElement);

    const changes: any = {};

    if (oldHash.geometry !== newHash.geometry) {
      changes.geometry = true;
    }
    if (oldHash.style !== newHash.style) {
      changes.style = true;
    }
    if (oldHash.info !== newHash.info) {
      changes.info = true;
    }

    return changes;
  },

  /**
   * Simple string hash function
   */
  hashString: (str: string): string => {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString();
  },

  /**
   * Batch multiple operations để minimize map re-renders
   */
  batchUpdate: async (updateFn: () => Promise<void>) => {
    // MapLibre GL automatically batches operations within same event loop
    // Just await the update function
    await updateFn();
  },

  /**
   * Update element trên map với specific changes
   */
  updateElementOnMap: async (element: Element_Base, changes: any) => {
    const sourceId = `element-source-${element.uuid}`;
    const layerIdPrefix = `element-layer-${element.uuid}`;

    try {
      // Update geometry if changed
      if (changes.geometry) {
        await ElementMapUtilOptimized.updateElementGeometry(element);
      }

      // Update style if changed
      if (changes.style) {
        await ElementMapUtilOptimized.updateElementStyle(element);
      }

      // Update info (name/description) if changed - affects label display
      if (changes.info) {
        await ElementMapUtilOptimized.updateElementInfo(element);
      }

    } catch (error) {
      console.error('Error updating element on map:', error);
      // Fallback: remove and re-add element
      await ElementMapUtilOptimized.removeElementFromMap(element);
      await ElementMapUtilOptimized.addElementToMap(element);
    }
  },

  /**
   * Update geometry của element using setData API
   */
  updateElementGeometry: async (element: Element_Base) => {
    const sourceId = `element-source-${element.uuid}`;

    if (!window.map?.getSource(sourceId)) {
      // Source doesn't exist, add element completely
      await ElementMapUtilOptimized.addElementToMap(element);
      return;
    }

    const geometry = JSON.parse(element.geom!);

    // Normalize geometry type
    if (geometry.type === 'point') geometry.type = 'Point';
    else if (geometry.type === 'polygon') geometry.type = 'Polygon';
    else if (geometry.type === 'linestring') geometry.type = 'LineString';

    const feature = {
      type: 'Feature',
      geometry: geometry,
      properties: {
        uuid: element.uuid,
        name: element.name,
        description: element.description,
      },
    };

    const geojsonData = {
      type: 'FeatureCollection',
      features: [feature]
    };

    // Update source data - MapLibre GL sẽ re-render automatically
    (window.map.getSource(sourceId) as maplibregl.GeoJSONSource).setData(geojsonData as any);
  },

  /**
   * Update info (name/description) of element - affects label display
   */
  updateElementInfo: async (element: Element_Base) => {
    const sourceId = `element-source-${element.uuid}`;

    if (!window.map?.getSource(sourceId)) {
      // Source doesn't exist, add element completely
      await ElementMapUtilOptimized.addElementToMap(element);
      return;
    }

    const geometry = JSON.parse(element.geom!);

    // Normalize geometry type
    if (geometry.type === 'point') geometry.type = 'Point';
    else if (geometry.type === 'polygon') geometry.type = 'Polygon';
    else if (geometry.type === 'linestring') geometry.type = 'LineString';

    const feature = {
      type: 'Feature',
      geometry: geometry,
      properties: {
        uuid: element.uuid,
        name: element.name,
        description: element.description,
      },
    };

    const geojsonData = {
      type: 'FeatureCollection',
      features: [feature]
    };

    // Update source data - MapLibre GL will re-render label automatically
    (window.map.getSource(sourceId) as maplibregl.GeoJSONSource).setData(geojsonData as any);
  },

  /**
   * Update style của element using setPaintProperty API
   */
  updateElementStyle: async (element: Element_Base) => {
    const layerIdPrefix = `element-layer-${element.uuid}`;

    let metadata = {};
    try {
      metadata = JSON.parse(element.metadata || '{}');
    } catch (error) {
      metadata = { style: {} };
    }

    // Ensure metadata has style property
    if (!(metadata as any).style) {
      (metadata as any).style = {};
    }

    const geometry = JSON.parse(element.geom!);
    const geometryType = geometry.type === 'point' ? 'Point' :
                        geometry.type === 'polygon' ? 'Polygon' :
                        geometry.type === 'linestring' ? 'LineString' : geometry.type;

    try {
      if (geometryType === 'Point') {
        const style = Element_Point_StyleMetadata_ModelUtil.normalize((metadata as any).style);

        // For points, we need to regenerate icon since it contains color/size
        // Remove old layer and re-add with new icon
        await ElementMapUtilOptimized.removeElementFromMap(element);
        await ElementMapUtilOptimized.addElementToMap(element);

      } else if (geometryType === 'LineString') {
        const style = Element_LineString_StyleMetadata_ModelUtil.normalize((metadata as any).style);
        const layerId = `${layerIdPrefix}-line`;

        if (window.map?.getLayer(layerId)) {
          window.map.setPaintProperty(layerId, 'line-width', style.strokeWidth);
          window.map.setPaintProperty(layerId, 'line-color', style.color);
          window.map.setPaintProperty(layerId, 'line-opacity', style.opacity);
        }

      } else if (geometryType === 'Polygon') {
        const style = Element_Polygon_StyleMetadata_ModelUtil.normalize((metadata as any).style);
        const fillLayerId = `${layerIdPrefix}-fill`;
        const outlineLayerId = `${layerIdPrefix}-outline`;

        if (window.map?.getLayer(fillLayerId)) {
          window.map.setPaintProperty(fillLayerId, 'fill-color', style.color);
          window.map.setPaintProperty(fillLayerId, 'fill-opacity', style.opacity);
        }

        if (window.map?.getLayer(outlineLayerId)) {
          window.map.setPaintProperty(outlineLayerId, 'line-width', style.strokeWidth);
          window.map.setPaintProperty(outlineLayerId, 'line-color', style.strokeColor);
          window.map.setPaintProperty(outlineLayerId, 'line-opacity', style.strokeOpacity);
        }
      }
    } catch (error) {
      console.error('Error updating element style:', error);
      // Fallback: remove and re-add
      await ElementMapUtilOptimized.removeElementFromMap(element);
      await ElementMapUtilOptimized.addElementToMap(element);
    }
  },

  /**
   * Add element to map - same as original but cache tracking
   */
  addElementToMap: async (element: Element_Base) => {
    const sourceId = `element-source-${element.uuid}`;
    const layerIdPrefix = `element-layer-${element.uuid}`;

    const geometry = JSON.parse(element.geom!);
    {
      if (geometry.type === 'point') {
        geometry.type = 'Point';
      } else if (geometry.type === 'polygon') {
        geometry.type = 'Polygon';
      } else if (geometry.type === 'linestring') {
        geometry.type = 'LineString';
      }
    }
    const feature = {
      type: 'Feature',
      geometry: geometry,
      properties: {
        uuid: element.uuid,
        name: element.name,
        description: element.description,
      },
    };
    const geojsonData: GeoJSONSourceSpecification = {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [feature] as GeoJSON.Feature[],
      } as GeoJSON.FeatureCollection,
    };

    // Remove if exists first (safety)
    ElementMapUtilOptimized.removeElementFromMap(element);
    window.map?.addSource(sourceId, geojsonData);

    let metadata = {};
    try {
      metadata = JSON.parse(element.metadata || '{}');
    } catch (error) {
      metadata = { style: {} };
    }

    // Ensure metadata has style property
    if (!(metadata as any).style) {
      (metadata as any).style = {};
    }

    // Point
    if (geometry.type === 'Point') {
      const style = Element_Point_StyleMetadata_ModelUtil.normalize((metadata as any).style);
      let imageName = `dot_${UUIDUtils.generateUUID()}`;
      {
        if (style.icon) {
          const defaultSize = 20;
          const iconSize = defaultSize * style.size;
          const bitmap = await IconUtil.getBitmap(<Icon icon={style.icon} size={iconSize} fill={style.color} />, iconSize, style.frame);
          window.map?.addImage(imageName, bitmap!)
        }
      }
      window.map?.addLayer({
        id: `${layerIdPrefix}-circle`,
        source: sourceId,
        type: 'symbol',
        layout: {
          'icon-image': imageName,
          'icon-size': 1,
          'icon-allow-overlap': true,
          'icon-anchor': 'center',
        },
      });

    }
    // LineString
    else if (geometry.type === 'LineString') {
      const style = Element_LineString_StyleMetadata_ModelUtil.normalize((metadata as any).style);
      window.map?.addLayer({
        id: `${layerIdPrefix}-line`,
        source: sourceId,
        type: 'line',
        paint: {
          'line-width': style.strokeWidth,
          'line-color': style.color,
          'line-opacity': style.opacity,
        },
      });
    }
    // Polygon
    else if (geometry.type === 'Polygon') {
      const style = Element_Polygon_StyleMetadata_ModelUtil.normalize((metadata as any).style);
      window.map?.addLayer({
        id: `${layerIdPrefix}-fill`,
        source: sourceId,
        type: 'fill',
        paint: {
          'fill-color': style.color,
          'fill-opacity': style.opacity,
        },
      });
      window.map?.addLayer({
        id: `${layerIdPrefix}-outline`,
        source: sourceId,
        type: 'line',
        paint: {
          'line-width': style.strokeWidth,
          'line-color': style.strokeColor,
          'line-opacity': style.strokeOpacity,
        },
      });
    }

    // Update cache
    ElementMapUtilOptimized.private_elementsOnMap.set(element.uuid, element);
    ElementMapUtilOptimized.private_elementHashes.set(element.uuid, ElementMapUtilOptimized.computeElementHash(element));
  },

  /**
   * Remove element from map - same as original but cache tracking
   */
  removeElementFromMap: (element: Element_Base) => {
    const sourceId = `element-source-${element.uuid}`;
    const layerIdPrefix = `element-layer-${element.uuid}`;
    if (!window.map) return;

    const layerIds = [`${layerIdPrefix}-circle`, `${layerIdPrefix}-line`, `${layerIdPrefix}-fill`, `${layerIdPrefix}-outline`];
    layerIds.forEach((layerId) => {
      if (window.map?.getLayer(layerId)) {
        window.map?.removeLayer(layerId);
      }
    });

    if (window.map.getSource(sourceId)) {
      window.map?.removeSource(sourceId);
    }

    // Update cache
    ElementMapUtilOptimized.private_elementsOnMap.delete(element.uuid);
    ElementMapUtilOptimized.private_elementHashes.delete(element.uuid);
  },

  /**
   * Clear all elements - for compatibility
   */
  clearAllElements: () => {
    const elements = Array.from(ElementMapUtilOptimized.private_elementsOnMap.values());
    elements.forEach(element => {
      ElementMapUtilOptimized.removeElementFromMap(element);
    });
  },

  /**
   * Get current elements on map - for debugging
   */
  getCurrentElementsOnMap: (): Element_Base[] => {
    return Array.from(ElementMapUtilOptimized.private_elementsOnMap.values());
  }
};